"use client";

import { Download } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheSafetyStandard() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const certifications = [{
    id: 1,
    code: "NEC",
    label: "NEC Compliant"
  }, {
    id: 2,
    code: "OSHA",
    label: "OSHA Certified"
  }, {
    id: 3,
    code: "ETL",
    label: "ETL Listed"
  }, {
    id: 4,
    code: "BBB",
    label: "BBB A+ Rated"
  }, {
    id: 5,
    code: "IEC",
    label: "IEC Standards"
  }, {
    id: 6,
    code: "NFPA",
    label: "NFPA 70E Trained"
  }, {
    id: 7,
    code: "ISO",
    label: "ISO Certified"
  }, {
    id: 8,
    code: "INS",
    label: "Liability Insured"
  }, {
    id: 9,
    code: "BOND",
    label: "Fully Bonded"
  }];
  const credentialLine = "LICENSE #EL-99201 · GENERAL LIABILITY $10M · WORKERS COMP CERTIFIED · UMBRELLA $20M";
  return <section data-section-id="900" id="the-safety-standard" className="bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div ref={leftRef} className={`lg:sticky lg:top-24 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div className="font-mono text-xs tracking-widest uppercase text-muted-foreground mb-8">
                04 — Safety & Standards
              </div>
              <h2 className="font-default text-4xl md:text-5xl font-bold leading-[1.05] tracking-tight text-foreground mb-8">
                Precision.
                <br />
                Power.
                <br />
                Protection.
              </h2>
              <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground mb-10 max-w-md">
                At Current Pulse Electric, safety is the foundation of every circuit we install. Our team adheres to the highest industry standards, ensuring every residential and commercial project is executed with rigorous precision, full insurance, and total accountability.
              </p>
              <Link to="/LetsTalkPage">
                <Button variant="outline" size="lg" className="border-2 border-secondary text-secondary hover:bg-primary hover:text-secondary-foreground transition-colors">
                  <Download className="size-4" />
                  Download Safety Specs (PDF)
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7" ref={rightRef}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
              {certifications.map((cert, i) => <div data-index={i} key={cert.id} className={`group bg-background hover:bg-primary transition-all duration-500 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
              transitionDelay: rightVisible ? `${i * 60}ms` : "0ms"
            }}>
                  <div className="p-8 flex flex-col items-center justify-center aspect-square">
                    <div className="flex-1 flex items-center justify-center w-full">
                      <span className="font-default text-2xl md:text-3xl font-bold tracking-tight text-foreground group-hover:text-secondary transition-colors duration-500">
                        {cert.code}
                      </span>
                    </div>
                    <div className="mt-6 pt-4 border-t border-border group-hover:border-secondary/20 w-full transition-colors duration-500">
                      <p className="font-mono text-[0.75rem] tracking-widest uppercase text-muted-foreground group-hover:text-secondary/70 text-center transition-colors duration-500">
                        {cert.label}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div className="mt-20 lg:mt-24 mb-8 h-px bg-border w-full" />

        {}
        <div className="relative w-full overflow-hidden">
          <div className="flex whitespace-nowrap animate-marquee">
            {Array.from({
            length: 4
          }).map((_, i) => <span data-index={i} key={i} className="font-mono text-xs md:text-sm tracking-widest uppercase text-muted-foreground px-8 shrink-0">
                {credentialLine}
              </span>)}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
          width: max-content;
        }
      `}</style>
    </section>;
}